/**
 * Adapter that projects a `UIMessage`/`AtlasMessage[]` thread onto the
 * read-only {@link ReadonlyTranscript} contract the flow engine folds over.
 *
 * The flow layer never reads UI parts directly — it sees only roles and the
 * tool parts (name, call id, state, input, output) surfaced here. Tool-part
 * detection and wire-name extraction go through the AI SDK's SSOT helpers
 * ({@link isToolUIPart}, {@link getToolName}) so static and dynamic tool parts
 * are handled identically.
 *
 * `latestToolPart` and `toolCallCount` consider only SETTLED parts (state
 * `'output-available'`) so flow derivation and signal reading are stable across
 * the streaming lifecycle of a call.
 */

import { getToolName, isToolUIPart, type UIMessage } from 'ai';

import type { ReadonlyToolPart, ReadonlyTranscript, ReadonlyTranscriptMessage } from './types.ts';

/** A settled tool part carries its result under `output`. */
const SETTLED_STATE = 'output-available';

/** Project one UI message's tool parts onto the read-only view. */
function toToolParts(message: UIMessage): ReadonlyToolPart[] {
  return message.parts.filter(isToolUIPart).map((part) => ({
    toolName: getToolName(part),
    toolCallId: part.toolCallId,
    state: part.state,
    input: part.input,
    output: 'output' in part ? part.output : undefined,
  }));
}

/** Project one UI message onto the read-only message view. */
function toMessage(message: UIMessage): ReadonlyTranscriptMessage {
  return { role: message.role, toolParts: toToolParts(message) };
}

/**
 * Wrap a `UIMessage[]` thread in a {@link ReadonlyTranscript}. All accessors are
 * pure; `latestToolPart` and `toolCallCount` see only settled parts.
 */
export function createTranscript(messages: readonly UIMessage[]): ReadonlyTranscript {
  const view: readonly ReadonlyTranscriptMessage[] = messages.map(toMessage);
  const allToolParts = view.flatMap((message) => message.toolParts);
  const settled = allToolParts.filter((part) => part.state === SETTLED_STATE);

  return {
    messages: view,
    toolParts: () => allToolParts,
    latestToolPart: () => settled.at(-1),
    toolCallCount: (toolName) => settled.filter((part) => part.toolName === toolName).length,
  };
}
